import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";

const POPUP_ID = "SN_SFA_ISSUE_REG_005_POP_006";
const QUERY_ID = "sn_sfa_issue_reg_005_pop_006";

const trim = (value: any) => String(value ?? "").trim();

const isClientTargetSelected = (row: any) => trim(row?.yn_selected) !== "N";

interface popupProps {
  modalRef?: any;
  noIssue?: string;
  selectedClientTargets?: any[];
}

const SN_SFA_ISSUE_REG_005_POP_006 = forwardRef(
  ({ modalRef, noIssue, selectedClientTargets }: popupProps, ref: React.Ref<any>) => {
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
    });
    const [columns, setColumns] = useState<any>({ grid1: [] });
    const [dataSources, setDataSources] = useState<any>({ grid1: [] });
    const [popupParent, setPopupParent] = useState<any>(null);
    const [loadStatus, setLoadStatus] = useState<
      "idle" | "loading" | "success" | "error"
    >("idle");

    const onSearch = useCallback(async () => {
      if (!noIssue) {
        setLoadStatus("error");
        setDataSources({ grid1: [] });
        return;
      }

      try {
        setLoadStatus("loading");
        const data: any = await fnGetDataSet({
          id: QUERY_ID,
          params: { no_issue: noIssue },
        });
        const rows = Array.isArray(data?.[0]) ? data[0] : [];
        if (Array.isArray(selectedClientTargets)) {
          const selectedKeys = new Set(
            selectedClientTargets
              .filter(isClientTargetSelected)
              .map((row: any) => String(row?.client_id ?? row?.cd_client ?? ""))
          );
          setDataSources({
            grid1: rows.map((row: any) => ({
              ...row,
              yn_selected: selectedKeys.has(
                String(row?.client_id ?? row?.cd_client ?? "")
              )
                ? "Y"
                : "N",
            })),
          });
          setLoadStatus("success");
          return;
        }
        setDataSources({
          grid1: rows.map((row: any) => ({
            ...row,
            yn_selected: isClientTargetSelected(row) ? "Y" : "N",
          })),
        });
        setLoadStatus("success");
      } catch {
        setLoadStatus("error");
        setDataSources({ grid1: [] });
      }
    }, [noIssue, selectedClientTargets]);

    useEffect(() => {
      setPopupParent(modalRef?.current?.getPopupParent?.() ?? null);
    }, [modalRef]);

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const rtn: any = await getInitPopUpData(POPUP_ID);
          setColumns((prev: any) => ({
            ...prev,
            grid1: rtn?.grid?.grid1 || [],
          }));
          await onSearch();
        } catch {
          setColumns((prev: any) => ({ ...prev, grid1: [] }));
          setDataSources({ grid1: [] });
          setLoadStatus("error");
        }
      };
      fetchInit();
    }, [onSearch]);

    const getSelectedValue = async () => {
      if (loadStatus !== "success") {
        await showDialog(
          "alter",
          "업체 선택 정보를 조회하지 못했습니다. 다시 조회 후 저장하세요."
        );
        return { excelUpload: false };
      }

      const grid = gridRefs.current["grid1"].current;
      grid?.stopEditing?.();

      const currentRows: any[] = [];
      if (grid?.forEachNode) {
        grid.forEachNode((node: any) => {
          if (node?.data) currentRows.push(node.data);
        });
      } else {
        currentRows.push(...(dataSources.grid1 ?? []));
      }

      return currentRows.map((row: any) => ({
        ...row,
        target_env: "ALL",
        yn_selected: isClientTargetSelected(row) ? "Y" : "N",
      }));
    };

    useImperativeHandle(ref, () => ({
      onSearch,
      getSelectedValue,
      onSave: async () => ({
        status: true,
        data: await getSelectedValue(),
      }),
    }));

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {},
        },
      }),
      [columns.grid1, dataSources.grid1]
    );

    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              title: "업체 선택",
              rowSelection: "multiple",
              popupParent: popupParent,
              enableInitialSort: false,
            }}
            events={grids.grid1.events}
          />
        </div>
      </div>
    );
  }
);

export default SN_SFA_ISSUE_REG_005_POP_006;
